/**
 * ONE CARD PER `web_self` VIEW, WRITTEN OUT BY HAND - which is the point of this file.
 *
 * `components/ViewCard.tsx` renders any view from whatever columns its rows happen to carry, and
 * that generality is exactly right for the staff inspector: a migration adding a column shows up
 * there with no front-end change, and an admin wants the column. It is the wrong shape for this
 * page. A player does not want `yaw`, `pitch`, `server_id`, a surrogate `id` or a pretty-printed
 * `payload` union; they want to know where their homes are, what is waiting for them, and which
 * base they log out in. Those are seven different sentences, and a component that knows nothing
 * about the view it is drawing cannot write any of them.
 *
 * WHAT THIS COSTS, STATED HONESTLY. A column added to `web_self.player_homes` tomorrow appears in
 * the staff inspector by itself and does NOT appear here until somebody decides how to say it. That
 * is the trade this page makes on purpose: the generic table is complete, and this page is legible.
 * A NEW VIEW cannot be forgotten, though - `MyProfilePage` holds a `Record` keyed by every self
 * view, so an eighth one stops the page compiling until it has a card here.
 *
 * THE STAFF PAGES ARE UNTOUCHED BY THIS FILE. Nothing here is imported by `/player/:uuid`, nothing
 * here reuses a `dc-` class beyond the shared font variables, and no component was "extracted for
 * reuse" between the two pages - the shared thing between them is the DATA (`SELF_VIEW_SPECS` for
 * the copy, the row types for the shape), never the presentation.
 *
 * READ-ONLY, AND SAYING SO WHERE IT MATTERS. The `web_player` Postgres role holds SELECT and
 * nothing else, so every card that a reader might expect to act on names the in-game command
 * instead of offering a control that would 403.
 */
import type {
  PlayerBaseTrustRow,
  PlayerHomeRow,
  PlayerLogoutPlacementRow,
  PlayerPendingRewardRow,
  PlayerRedemptionRow,
  PlayerSecretBaseRow,
  PlayerTeleportPrefRow,
} from '@dimensionscore/shared';

import type { PolledView } from '../api/usePolledView';
import type { SelfViewSpec } from '../player/viewSpecs';

import { ItemGlyph } from './ItemGlyph';
import { coordinates, dimensionLabel, friendlyInstant, isoOf, itemInitials, itemLabel, titleCase } from './format';

/** What every card below takes: its copy, and the polled state of its one view. */
export interface SectionProps<T> {
  readonly spec: SelfViewSpec;
  readonly state: PolledView<T>;
}

interface MeCardProps<T> extends SectionProps<T> {
  /**
   * What to do IN GAME to fill this card, shown only when it is empty.
   *
   * Not optional decoration: a brand-new player's page is seven empty cards, and "You have not set
   * any homes yet" followed by nothing is a dead end. The hint names a real command from the mod's
   * own dispatcher - `/sethome`, `/rewards claimall`, `/sb create`, `/mysterygift`, `/tpatoggle` -
   * so it is an instruction that works rather than a plausible-looking one.
   */
  readonly hint: React.ReactNode;
  readonly children: (rows: readonly T[]) => React.JSX.Element;
}

/**
 * The frame every card shares: heading, count, caption, and the four states a polled view has.
 *
 * The `data-testid`s are deliberately the SAME as the generic table's (`view-<name>`,
 * `view-empty-<name>`, `view-error-<name>`, `view-row`), because three suites - the jsdom tests,
 * the preview Playwright specs and the real-Postgres stack spec - assert against them, and those
 * assertions are about which views a player may read, not about which markup renders them. Keeping
 * the hooks means the redesign changed the appearance and not the contract.
 */
function MeCard<T>({ spec, state, hint, children }: MeCardProps<T>): React.JSX.Element {
  const rows = state.rows ?? [];
  const headingId = `me-heading-${spec.view}`;

  return (
    <section className="me-card" id={`view-${spec.view}`} aria-labelledby={headingId} data-testid={`view-${spec.view}`} data-view={spec.view}>
      <div className="me-card-head">
        <h2 id={headingId}>{spec.title}</h2>
        {rows.length > 0 && (
          <span className="me-count" data-testid={`view-count-${spec.view}`}>
            {rows.length}
          </span>
        )}
      </div>
      <p className="me-caption">{spec.caption}</p>

      {state.status === 'error' ? (
        /*
         * A FAILED READ IS NOT THE PLAYER'S PROBLEM, so the first line is what it means for them
         * and the second is the detail somebody helping them will ask for. The page keeps polling,
         * so this really does heal by itself when the sidecar comes back.
         */
        <p className="me-state" role="status" data-testid={`view-error-${spec.view}`}>
          <span>This part did not load just now. The page keeps trying, so it may fill in on its own.</span>
          <span className="me-state-detail">{state.error}</span>
        </p>
      ) : state.status !== 'ready' && rows.length === 0 ? (
        <p className="me-skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </p>
      ) : rows.length === 0 ? (
        <div className="me-empty" data-testid={`view-empty-${spec.view}`}>
          <p className="me-empty-text">{spec.emptyText}</p>
          <p className="me-empty-hint">{hint}</p>
        </div>
      ) : (
        children(rows)
      )}
    </section>
  );
}

/** A timestamp as a sentence fragment, with the machine-readable original still in the DOM. */
function When({ prefix, value }: { prefix: string; value: unknown }): React.JSX.Element | null {
  const said = friendlyInstant(value);
  const iso = isoOf(value);
  if (said === null || iso === null) return null;
  return (
    <>
      {prefix}{' '}
      <time dateTime={iso} title={iso}>
        {said}
      </time>
    </>
  );
}

/* ------------------------------------------------------------------------------------- homes */

export function HomesCard({ spec, state }: SectionProps<PlayerHomeRow>): React.JSX.Element {
  return (
    <MeCard
      spec={spec}
      state={state}
      hint={
        <>
          Stand somewhere you want to come back to and run <code>/sethome name</code>.
        </>
      }
    >
      {(rows) => (
        <ul className="me-grid">
          {rows.map((row) => {
            const dimension = dimensionLabel(row.dimension);
            const where = coordinates(row.x, row.y, row.z);
            return (
              <li key={row.id} className="me-item" data-testid="view-row">
                <p className="me-item-title">{row.name}</p>
                {dimension !== null && (
                  <p className="me-item-line">
                    <span className="me-pill" title={row.dimension}>
                      {dimension}
                    </span>
                  </p>
                )}
                {where !== null && <p className="me-coords">{where}</p>}
                <p className="me-item-foot">
                  <code>/home {row.name}</code>
                  {friendlyInstant(row.created_at) !== null && (
                    <>
                      {' · '}
                      <When prefix="saved" value={row.created_at} />
                    </>
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </MeCard>
  );
}

/* --------------------------------------------------------------------------- reward mailbox */

export function MailboxCard({ spec, state }: SectionProps<PlayerPendingRewardRow>): React.JSX.Element {
  return (
    <MeCard
      spec={spec}
      state={state}
      hint={
        <>
          Rewards land here when you are offline, or when your inventory was full. Run <code>/rewards claimall</code> next time you are in game.
        </>
      }
    >
      {(rows) => (
        <>
          <ul className="me-list">
            {rows.map((row) => {
              const itemId = row.display_item?.itemId ?? null;
              const item = itemLabel(itemId);
              const count = row.display_item?.count ?? null;
              return (
                <li key={row.id} className="me-row" data-testid="view-row">
                  <ItemGlyph itemId={itemId} initials={itemInitials(itemId)} />
                  <div className="me-row-body">
                    {/* `description` is optional in the schema; a reward with none is still a real
                        reward, and "A reward is waiting" beats an empty line above a date. */}
                    <p className="me-item-title">{row.description ?? 'A reward is waiting'}</p>
                    {item !== null && (
                      <p className="me-item-line">
                        <span className="me-pill" title={itemId ?? undefined}>
                          {count !== null && count > 1 ? `${item} × ${count}` : item}
                        </span>
                      </p>
                    )}
                    <p className="me-item-foot">
                      <When prefix="waiting since" value={row.created_at} />
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="me-note">
            Collect them in game with <code>/rewards claimall</code> — this page cannot claim anything.
          </p>
        </>
      )}
    </MeCard>
  );
}

/* ------------------------------------------------------------------------------ gift codes */

export function RedemptionsCard({ spec, state }: SectionProps<PlayerRedemptionRow>): React.JSX.Element {
  return (
    <MeCard
      spec={spec}
      state={state}
      hint={
        <>
          When the server hands out a code, redeem it in game with <code>/mysterygift code</code>. Each code works once per player.
        </>
      }
    >
      {(rows) => (
        <ul className="me-list">
          {rows.map((row) => (
            <li key={row.id} className="me-row" data-testid="view-row">
              <div className="me-row-body">
                <p className="me-item-title">{row.code}</p>
                <p className="me-item-foot">
                  <When prefix="redeemed" value={row.redeemed_at} />
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </MeCard>
  );
}

/* ---------------------------------------------------------------------------- secret bases */

export function BasesCard({ spec, state }: SectionProps<PlayerSecretBaseRow>): React.JSX.Element {
  return (
    <MeCard
      spec={spec}
      state={state}
      hint={
        <>
          A base is your own private world. Run <code>/sb create</code> in game to start one, then <code>/sb</code> to go there.
        </>
      }
    >
      {(rows) => (
        <ul className="me-grid" data-wide="true">
          {rows.map((row) => {
            const status = titleCase(row.status);
            const template = titleCase(row.template_key);
            return (
              <li key={row.id} className="me-item" data-testid="view-row">
                <p className="me-item-title">{row.name}</p>
                <p className="me-item-line">
                  {/* "Default" rather than "where /sb takes you": the command belongs in the foot
                      line below, where every other card puts it, and a pill with a code inside it
                      is two kinds of emphasis fighting. */}
                  {row.is_default === true && (
                    <span className="me-pill" data-tone="accent">
                      Default
                    </span>
                  )}
                  {status !== null && <span className="me-pill">{status}</span>}
                  {template !== null && <span className="me-pill">{template}</span>}
                  {typeof row.size_level === 'number' && <span className="me-pill">Size {row.size_level}</span>}
                  {/* Both flags say their meaning in words; the visitor one matters most to a
                      player, because it is the difference between a private world and a public one. */}
                  {row.public_flag === true && <span className="me-pill">Open to visitors</span>}
                  {row.public_flag === false && <span className="me-pill">Private</span>}
                  {row.locked === true && <span className="me-pill">Locked</span>}
                </p>
                {/*
                  ONLY THE DEFAULT BASE GETS A COMMAND, because only that one has an unambiguous
                  one: bare `/sb` teleports to the default (`SecretBasesCommand.teleportDefault`).
                  Reaching a SECOND base of your own goes through `/sb visit <player> <name>`, and
                  printing a half-remembered form of it would be worse than printing nothing.
                */}
                <p className="me-item-foot">
                  {row.is_default === true && (
                    <>
                      <code>/sb</code> takes you straight here
                      {friendlyInstant(row.last_entered_at) !== null && ' · '}
                    </>
                  )}
                  {friendlyInstant(row.last_entered_at) !== null && <When prefix="last visited" value={row.last_entered_at} />}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </MeCard>
  );
}

/* ------------------------------------------------------------------------------- trusted on */

export function TrustsCard({ spec, state }: SectionProps<PlayerBaseTrustRow>): React.JSX.Element {
  return (
    <MeCard
      spec={spec}
      state={state}
      hint={<>Somebody who trusts you with their base can add you with a single command, and it appears here.</>}
    >
      {(rows) => (
        <ul className="me-list">
          {rows.map((row) => {
            const owner = row.base_owner_name;
            return (
              <li key={row.id} className="me-row" data-testid="view-row">
                <div className="me-row-body">
                  <p className="me-item-title">{row.base_name}</p>
                  <p className="me-item-foot">
                    {/* The owner's name is shown because they CHOSE to trust this player; it is not
                        a lookup of somebody else's rows. Their UUID is not shown - the name is what
                        `/sb visit` takes. */}
                    {owner === null ? 'Owned by another player' : `Owned by ${owner}`}
                    {owner !== null && (
                      <>
                        {' · '}
                        <code>/sb visit {owner}</code>
                      </>
                    )}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </MeCard>
  );
}

/* -------------------------------------------------------------------------- logout placement */

/**
 * At most one row, and its THREE states are the reason this view exists separately from the bases
 * card (see `PlayerLogoutPlacementRow`): logged out in your own base, logged out in somebody
 * else's, or the base you were in has been deleted and the server will move you out on your next
 * join. The third is the one worth a plain sentence - it is the only thing on this page that will
 * visibly happen to a player without them asking.
 */
export function LogoutCard({ spec, state, selfUuid }: SectionProps<PlayerLogoutPlacementRow> & { selfUuid: string | null }): React.JSX.Element {
  return (
    <MeCard spec={spec} state={state} hint={<>Log out inside a base and it will be remembered here, so you wake up where you left off.</>}>
      {(rows) => {
        const row = rows[0];
        if (row === undefined) return <p className="me-statement">{spec.emptyText}</p>;
        const owned = row.base_owner_uuid !== null && selfUuid !== null && row.base_owner_uuid === selfUuid;
        return (
          <div data-testid="view-row">
            <p className="me-statement">
              {row.base_uuid === null ? (
                <>
                  The base you logged out in <strong>no longer exists</strong>. Nothing is lost — the server will put you somewhere safe the next time
                  you join.
                </>
              ) : owned ? (
                <>
                  You logged out inside your own base, <strong>{row.base_name}</strong>, and that is where you will wake up.
                </>
              ) : (
                <>
                  You logged out inside <strong>{row.base_name}</strong>
                  {row.base_owner_name === null ? ', a base belonging to another player' : `, ${row.base_owner_name}'s base`}, and that is where you
                  will wake up.
                </>
              )}
            </p>
          </div>
        );
      }}
    </MeCard>
  );
}

/* ----------------------------------------------------------------------- teleport preference */

export function TeleportCard({ spec, state }: SectionProps<PlayerTeleportPrefRow>): React.JSX.Element {
  return (
    <MeCard
      spec={spec}
      state={state}
      hint={
        <>
          Teleport requests are accepted unless you turn them off, which is why there is no setting stored for you yet. <code>/tpatoggle</code> is
          the switch.
        </>
      }
    >
      {(rows) => {
        const blocked = rows[0]?.tpa_blocked === true;
        return (
          <div data-testid="view-row">
            <p className="me-statement">
              {blocked ? (
                <>
                  Teleport requests to you are <strong>turned off</strong>. Nobody can <code>/tpa</code> to you until you turn them back on.
                </>
              ) : (
                <>
                  Teleport requests to you are <strong>accepted</strong>. Anyone can ask with <code>/tpa</code>, and you decide each time.
                </>
              )}
            </p>
            <p className="me-note">
              Change it in game with <code>/tpatoggle</code>. This page can only read it.
            </p>
          </div>
        );
      }}
    </MeCard>
  );
}
